import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import {
  theme,
  useResponsive,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import {
  GiftCard,
  useRedeemGiftCardDefaultValues,
} from '@order/features/gift-card';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import {
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
} from '../../components';
import {
  useGiftCardBalance,
  useGiftCardContent,
  useGiftCardOrders,
} from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountGiftCardsScreen = () => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const { track } = useTelemetry();

  // ─── Queries ───────────────────────────────────────────────────────────────

  const giftCardBalanceData = useGiftCardBalance();
  const giftCardOrdersData = useGiftCardOrders();
  const contentfulData = useGiftCardContent();

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const handleOnRequestGiftCardPurchase = useCallback(() => {
    track('account.gift.header.pressed');

    if (isExpoRouterEnabled) {
      router?.dismissAll();
      router?.navigate('/gift');
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'GiftCardsTab',
      params: { screen: 'GiftCards' },
    });
  }, [track, isExpoRouterEnabled, router, navigation]);

  const handleOnRequestGiftCardRedemption = useCallback(() => {
    track('account.gift.redemption.pressed');

    if (isExpoRouterEnabled) {
      router?.navigate('/add-gift-card');
      return;
    }

    navigation?.navigate('Modal', { screen: 'AddGiftCard' });
  }, [track, isExpoRouterEnabled, router, navigation]);

  const getConfirmationLink = useCallback((id: string) => {
    return {
      screen: 'MainTabs' as const,
      params: {
        screen: 'GiftCardsTab' as const,
        params: {
          screen: 'GiftCardConfirmation' as const,
          params: { orderId: id, referrer: 'gift-card-orders' as const },
          initial: false,
        },
      },
    };
  }, []);

  const handleOrderPress = useCallback(
    (orderId: string) => {
      router?.navigate(
        `/(tabs)/gift/confirmation/${orderId}?referrer=gift-card-orders`,
      );
    },
    [router],
  );

  // ─── Gift Cards ────────────────────────────────────────────────────────────

  const routeParams = useSafeRouteParams<{ giftCardId?: string }>();
  const giftCardRedemptionEncodedData = routeParams?.giftCardId;

  const defaultRedemptionValues = useRedeemGiftCardDefaultValues({
    giftCardRedemptionEncodedData,
  });

  // ─── Rendering ─────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainer screen="gift-cards">
      {currentBreakpoint.isXS ? (
        <AccountScreenTitle title={formatMessage(messages.title)} />
      ) : null}

      <AccountScreenSection style={styles.content}>
        <GiftCard.Account.Header
          {...contentfulData}
          onPress={handleOnRequestGiftCardPurchase}
        />

        <GiftCard.Account.Info
          {...giftCardBalanceData}
          {...defaultRedemptionValues}
          onRedeemGiftCardPress={handleOnRequestGiftCardRedemption}
        />

        <GiftCard.Account.Orders
          {...giftCardOrdersData}
          onOpenGiftCardsScreen={handleOnRequestGiftCardPurchase}
          getConfirmationLink={getConfirmationLink}
          onOrderPress={isExpoRouterEnabled ? handleOrderPress : undefined}
        />
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    gap: theme.spacing['10'],
    paddingBottom: theme.spacing['6'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Gift Cards',
    description: 'Gift Cards Screen | Title',
  },
});
